import { FC, useState, useEffect, useCallback, useContext, useRef } from 'react';
import clsx from 'clsx';
import { Button, Container, Grid, makeStyles, Theme, Tooltip, Typography } from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';
import useOpenRecord from 'hooks/useOpenRecord';
import { TabRecordType, buildTabKey } from 'constants/tabs';
import useRouter from 'hooks/useRouter';
import {
  GET_CONFIRM_SERVICE_URL,
  GET_DELETE_SERVICES_URL,
  GET_JOB_DETAIL_BY_ADDITIONAL_SERVICE_ID,
  GET_SERVICE_DETAIL_BY_ID,
  GET_UNCONFIRM_SERVICE_URL
} from 'constants/url';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ViewIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import RenewIcon from '@material-ui/icons/Autorenew';
import ConfirmIcon from '@material-ui/icons/Check';
import UnapproveIcon from '@material-ui/icons/Undo';
import DeleteIcon from '@material-ui/icons/Delete';
import TransferIcon from '@material-ui/icons/SwapHoriz';

import ActionSnackbar from 'components/ActionSnackbar';
import Breadcrumb from 'components/Breadcrumb';
import ShowPdfModal from 'components/ShowPdfModal';
import ShowPdfTypes from 'typings/ShowPdfTypes';
import { dummyServiceDetailBody } from 'constants/dummy';
import Content from './components/Content';
import LoadingButtonIndicator from 'components/LoadingButtonIndicator';
import SideBarContent from 'components/SideBarContent';
import { getDeleteQuotationMessage, hasAccessPermission, isQuotationDeletable, ucWords } from 'utils';
import EditChecklistForm from './components/EditForm/EditChecklistForm';
import EditScheduleForm from './components/EditForm/EditScheduleForm';
import EditServiceForm from './components/EditForm/EditServiceForm';
import { JobStatus, ServiceType } from 'constants/enum';
import ServiceForm from 'pages/ServiceForm';
import CustomizedDialog from 'components/CustomizedDialog';
import InvoiceForm from './components/EditForm/InvoiceForm';
import theme from 'theme';
import { StandardConfirmationDialog } from 'components/AppDialog';
import PasswordConfirmation from 'components/PasswordConfirmation';
import TransferQuotationDialog from 'components/TransferQuotationDialog';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { useIsTabActive, useTabs } from 'contexts/TabContext';
import { getCurrentRoleGrants } from 'selectors';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  container: {
    '& > :nth-child(n+2)': {
      marginTop: theme.spacing(2)
    }
  },
  marginRight: {
    marginRight: theme.spacing(1)
  },
  marginLeft: {
    marginLeft: theme.spacing(1)
  }
}));

interface Props {
  /** Supplied when the page is rendered inside a tab, where there is no route match to read. */
  recordId?: string;
}

const ServiceDetailPage: FC<Props> = props => {
  const openRecord = useOpenRecord();
  const { match, history } = useRouter();
  const params = props.recordId || match.params.id;

  /** Rendered inside a tab rather than straight off a route. */
  const isInsideTab = !!props.recordId;
  const isTabActive = useIsTabActive();
  const { closeTab, refreshPinnedTab } = useTabs();

  const [isRecordMissing, setRecordMissing] = useState<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const classes = useStyles();
  const { currentUser } = useContext(CurrentUserContext);
  const currentRoleGrants = getCurrentRoleGrants(currentUser);
  const isDeleteActive = hasAccessPermission('QUOTATIONS', 'DELETE', currentRoleGrants);
  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [service, setService] = useState<ServiceDetailModel>(dummyServiceDetailBody);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarVarient, setSnackbarVarient] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarIsCountdown, setSnackbarIsCountdown] = useState<boolean>(false);

  const [openForm, setOpenForm] = useState<boolean>(false);
  const [form, setForm] = useState<string>('');
  const [isEditable, setIsEditable] = useState<boolean>(true);
  const [openPdfModal, setOpenPdfModal] = useState<boolean>(false);

  const [isRenew, setIsRenew] = useState<boolean>(false);
  const [serviceId, setServicetId] = useState<number>(0);
  const [, setServiceType] = useState<string>('');
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [openUnapproveConfirmation, setOpenUnapproveConfirmation] = useState<boolean>(false);
  const [openInvoiceForm, setOpenInvoiceForm] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState<boolean>(false);
  const [openTransferDialog, setOpenTransferDialog] = useState<boolean>(false);

  const hasCompletedJob = service.Jobs.some(job => job.jobStatus === JobStatus.COMPLETED);
  // The quotation info stays editable after the invoice is fully paid: its text can always change, while its
  // amount, entity and address need the edit-paid-invoice permission and a password (see EditServiceForm).

  const fetchData = useCallback(() => {
    setIsLoading(true);

    const loadData = async () => {
      try {
        const { data } = await axios.get(`${GET_SERVICE_DETAIL_BY_ID(params)}`, { cancelToken: cancelTokenSource.token });

        // Closing a tab unmounts this page mid-flight. Bail out rather than setting state on a
        // component that is already gone. Cancelling the request instead is not an option here:
        // cancelTokenSource is rebuilt on every render, so cancelling would kill live requests.
        if (!isMountedRef.current) {
          return;
        }

        const jobs: JobModel[] = data.Jobs;
        // const isJobCompeleted = jobs.some(value => value.jobStatus === JobStatus.COMPLETED);

        if (data.serviceStatus === 'CANCELLED' || data.serviceType === ServiceType.ADDITIONAL) {
          setIsEditable(false);
        }

        if (jobs.length === 0) {
          const additionalJob = await axios.get(`${GET_JOB_DETAIL_BY_ADDITIONAL_SERVICE_ID(params)}`, { cancelToken: cancelTokenSource.token });
          const Jobs = [
            {
              id: additionalJob.data.jobId,
              jobStatus: additionalJob.data.jobStatus,
              startDateTime: additionalJob.data.startDateTime,
              endDateTime: additionalJob.data.endDateTime,
              ChecklistJob: additionalJob.data.ChecklistJob ? additionalJob.data.ChecklistJob : [],
              serviceItemsJob: additionalJob.data.AdditionalServiceItem ? additionalJob.data.AdditionalServiceItem : [],
              JobLabels: additionalJob.data.JobLabel ? additionalJob.data.JobLabel : [],
              jobAmount: additionalJob.data.additionalAmount ? additionalJob.data.additionalAmount : 0
            }
          ];

          // A second await means a second chance for the tab to have been closed underneath us.
          if (!isMountedRef.current) {
            return;
          }

          setService({ ...data, Jobs });
        } else {
          setService(data);
        }

        setIsLoading(false);
      } catch (err) {
        console.log(err);
        const error = err as any;
        // A cancelled request rejects with the cancel object, which carries no data — reading
        // error.data blindly is what turned a closing tab into "error.data is undefined".
        const errorCode = error && error.data ? error.data.errorCode : undefined;

        if (errorCode === 11 || errorCode === 12) {
          // Inside a tab this must stay local. Sending the whole app to /notfound would drag every
          // other open tab along with it, and a background tab could do it without being looked at.
          if (isInsideTab) {
            if (isMountedRef.current) {
              setRecordMissing(true);
            }
          } else {
            history.push({ pathname: `/notfound/` });
          }
        }

        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelTokenSource.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSnackbar = (variant: 'success' | 'error', message: string, isCountdown?: boolean) => {
    /**
     * A countdown snackbar means "we are leaving this record" — today only the delete flow uses it,
     * and it works by pushing the whole app to the quotation list. Inside a tab that is the wrong
     * move: the list is already open behind this tab. Close the tab instead and reload the list, so
     * it stops showing a row that no longer exists.
     */
    if (isInsideTab && isCountdown && variant === 'success') {
      refreshPinnedTab();
      closeTab(buildTabKey(TabRecordType.QUOTATION, params));
      return;
    }

    setOpenSnackbar(true);
    setSnackbarVarient(variant);
    setSnackbarMessage(message);
    if (isCountdown) {
      setSnackbarIsCountdown(isCountdown);
    }
  };

  const handleOpenForm = (form: string) => {
    setOpenForm(true);
    setForm(form);
  };

  const handleConfirmAction = async (serviceId: number) => {
    setIsLoading(true);
    try {
      await axios.put(`${GET_CONFIRM_SERVICE_URL(serviceId)}`, { cancelToken: cancelTokenSource.token });
      setOpenConfirmation(false);
      fetchData();
      handleSnackbar('success', 'Successfully approve quotation');
    } catch (err) {
      console.log(err);
      handleSnackbar('error', 'Failed approve quotation');
    }
    setIsLoading(false);
  };

  const handleUnapproveAction = async (serviceId: number) => {
    setIsLoading(true);
    try {
      await axios.put(`${GET_UNCONFIRM_SERVICE_URL(serviceId)}`, { cancelToken: cancelTokenSource.token });
      setOpenUnapproveConfirmation(false);
      fetchData();
      handleSnackbar('success', 'Successfully unapprove quotation');
    } catch (err) {
      console.log(err);
      handleSnackbar('error', 'Failed unapprove quotation');
    }
    setIsLoading(false);
  };

  const handleViewDetailAction = () => {
    openRecord({ recordType: TabRecordType.QUOTATION, recordId: serviceId });
    setOpenSuccessModal(false);
  };

  const handleDelete = () => {
    setOpenPasswordDialog(true);
    setIsDelete(false);
  };

  const handleCloseDelete = () => {
    setIsDelete(false);
    setOpenPasswordDialog(false);
  };

  /**
   * The record is gone — deleted by someone else, or by this user in another tab. Say so inside the
   * tab and let them close it, rather than moving the whole app somewhere they did not ask to go.
   */
  if (isRecordMissing) {
    return (
      <Container maxWidth={false} className={clsx(classes.root, classes.container)}>
        <Typography variant='h6' gutterBottom>
          This quotation no longer exists
        </Typography>
        <Typography variant='body2' color='textSecondary' gutterBottom>
          It may have been deleted. Close this tab to carry on with the others.
        </Typography>
        <Button variant='contained' color='primary' disableElevation onClick={() => closeTab(buildTabKey(TabRecordType.QUOTATION, params))}>
          Close Tab
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className={clsx(classes.root, classes.container)}>
      <Grid container spacing={3} alignItems='center'>
        <Grid item sm={6}>
          <Typography variant='h4' gutterBottom>
            Quotation Details
          </Typography>
          <Breadcrumb pages={['quotations', service.clientName]} />
        </Grid>
        <Grid item container sm={12}>
          <Grid item sm={8}>
            <Button
              variant='contained'
              disabled={isLoading}
              disableElevation
              className={classes.marginRight}
              startIcon={<ViewIcon />}
              onClick={() => setOpenPdfModal(true)}
            >
              View PDF
              <LoadingButtonIndicator isLoading={isLoading} />
            </Button>
            {service.serviceStatus === 'PENDING' && (
              <Button
                variant='contained'
                disabled={isLoading || !isEditable}
                disableElevation
                className={classes.marginRight}
                startIcon={<ConfirmIcon />}
                onClick={() => setOpenConfirmation(true)}
              >
                Approve Quotation
                <LoadingButtonIndicator isLoading={isLoading} />
              </Button>
            )}
            {service.serviceStatus !== 'PENDING' && hasCompletedJob && (
              <Tooltip
                title={
                  !isEditable
                    ? service.serviceType === ServiceType.ADDITIONAL
                      ? 'Cannot edit because this is separate quotation'
                      : 'Cannot edit because quotation already have invoice or quotation is cancelled'
                    : ''
                }
              >
                <span style={{ cursor: !isEditable ? 'not-allowed' : 'default' }}>
                  <Button
                    variant='contained'
                    disabled={isLoading || !isEditable}
                    disableElevation
                    className={classes.marginRight}
                    startIcon={<RenewIcon />}
                    onClick={() => {
                      setIsRenew(true);
                      handleOpenForm('renew contract');
                    }}
                  >
                    Renew Quotation
                    <LoadingButtonIndicator isLoading={isLoading} />
                  </Button>
                </span>
              </Tooltip>
            )}
            {service.serviceStatus !== 'PENDING' && !hasCompletedJob && (
              <Button
                variant='contained'
                disabled={isLoading}
                disableElevation
                className={classes.marginRight}
                startIcon={<UnapproveIcon />}
                onClick={() => setOpenUnapproveConfirmation(true)}
              >
                Unapprove Quotation
                <LoadingButtonIndicator isLoading={isLoading} />
              </Button>
            )}
            {service.invoiceNumber === null && service.serviceType !== ServiceType.ADDITIONAL && (
              <Button
                variant='contained'
                disabled={isLoading}
                disableElevation
                className={classes.marginRight}
                startIcon={<TransferIcon />}
                onClick={() => setOpenTransferDialog(true)}
              >
                Transfer Quotation
                <LoadingButtonIndicator isLoading={isLoading} />
              </Button>
            )}
            {isDeleteActive && isQuotationDeletable(service.invoiceStatus, service.invoiceCollectedAmount) && (
              <Button
                variant='contained'
                disabled={isLoading}
                disableElevation
                style={{ background: theme.palette.error.main, color: '#ffffff' }}
                startIcon={<DeleteIcon />}
                onClick={() => setIsDelete(true)}
              >
                Delete Quotation
                <LoadingButtonIndicator isLoading={isLoading} />
              </Button>
            )}
          </Grid>
          <Grid item container justify='flex-end' sm={4}>
            <Tooltip
              title={
                !isEditable
                  ? service.serviceType === ServiceType.ADDITIONAL
                    ? 'Cannot edit because this is separate quotation'
                    : 'Cannot edit because the quotation is cancelled'
                  : ''
              }
            >
              <span style={{ cursor: !isEditable ? 'not-allowed' : 'default' }}>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={isLoading || !isEditable}
                  disableElevation
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenForm('edit details')}
                >
                  Edit Quotation Info
                  <LoadingButtonIndicator isLoading={isLoading} />
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      <Content
        isLoading={isLoading}
        service={service}
        isEditable={isEditable}
        handleOpenForm={handleOpenForm}
        setOpenInvoiceForm={setOpenInvoiceForm}
        handleSnackbar={handleSnackbar}
      />
      {isTabActive && openPdfModal && (
        <ShowPdfModal
          id={Number(service.id)}
          serviceId={Number(service.id)}
          documentType={ShowPdfTypes.SERVICE}
          documentName={service.serviceTitle}
          open={openPdfModal}
          handleClose={() => setOpenPdfModal(false)}
          handleSnackbar={handleSnackbar}
        />
      )}
      <SideBarContent
        title={ucWords(form)}
        open={isTabActive && openForm}
        onClickDrawer={() => setOpenForm(false)}
        width={form.includes('renew') ? '100%' : form.includes('schedules') ? '80%' : '70%'}
      >
        {form.includes('renew') ? (
          <ServiceForm
            isRenew={isRenew}
            oldContractId={service.id}
            clientId={Number(service.clientId)}
            setServiceId={setServicetId}
            setServiceType={setServiceType}
            setOpenSuccessModal={setOpenSuccessModal}
            fetchData={fetchData}
            handleCancel={() => setOpenForm(false)}
            handleSnackbar={handleSnackbar}
          />
        ) : form.includes('details') ? (
          <EditServiceForm
            service={service}
            setService={setService}
            handleClose={() => setOpenForm(false)}
            handleSnackbar={handleSnackbar}
            fetchData={fetchData}
          />
        ) : form.includes('schedules') ? (
          <EditScheduleForm service={service} handleClose={() => setOpenForm(false)} handleSnackbar={handleSnackbar} fetchData={fetchData} />
        ) : (
          <EditChecklistForm serviceId={service.id} handleCancel={() => setOpenForm(false)} handleSnackbar={handleSnackbar} fetchData={fetchData} />
        )}
      </SideBarContent>
      {isTabActive && openSuccessModal && (
        <CustomizedDialog
          isLoading={isLoading}
          open={openSuccessModal}
          isConfirmation
          variant='success'
          title='Successfully!'
          message={`Your quotation has been renewed with ID: ${serviceId}`}
          primaryButtonLabel='Approve Quotation'
          secondaryButtonLabel='Done & View Details'
          primaryActionButton={() => {
            handleConfirmAction(serviceId);
            setOpenSuccessModal(false);
          }}
          secondaryActionButton={handleViewDetailAction}
          handleClose={() => setOpenSuccessModal(false)}
        />
      )}
      {isTabActive && openConfirmation && (
        <CustomizedDialog
          isLoading={isLoading}
          open={openConfirmation}
          isConfirmation
          variant='warning'
          title='Approve Quotation'
          message={'Are you sure you want to approve this quotation?'}
          primaryButtonLabel='Ok'
          secondaryButtonLabel='Cancel'
          primaryActionButton={() => handleConfirmAction(service.id)}
          secondaryActionButton={() => setOpenConfirmation(false)}
          handleClose={() => setOpenConfirmation(false)}
        />
      )}
      {isTabActive && openUnapproveConfirmation && (
        <CustomizedDialog
          isLoading={isLoading}
          open={openUnapproveConfirmation}
          isConfirmation
          variant='warning'
          title='Unapprove Quotation'
          message={'Are you sure you want to unapprove this quotation? The status will be reverted to PENDING.'}
          primaryButtonLabel='Ok'
          secondaryButtonLabel='Cancel'
          primaryActionButton={() => handleUnapproveAction(service.id)}
          secondaryActionButton={() => setOpenUnapproveConfirmation(false)}
          handleClose={() => setOpenUnapproveConfirmation(false)}
        />
      )}
      {isTabActive && openInvoiceForm && (
        <InvoiceForm
          open={openInvoiceForm}
          service={service}
          loadData={fetchData}
          handleCancel={() => setOpenInvoiceForm(false)}
          handleSnackbar={handleSnackbar}
        />
      )}
      {isTabActive && isDelete && (
        <StandardConfirmationDialog
          variant={'warning'}
          title='Confirm Quotation Deletion'
          message={getDeleteQuotationMessage(!!service.invoiceNumber, service.serviceType === ServiceType.ADDITIONAL)}
          okLabel='OK'
          cancelLabel='cancel'
          open={isDelete}
          handleClose={() => setIsDelete(false)}
          onConfirm={handleDelete}
        />
      )}
      {isTabActive && openTransferDialog && (
        <TransferQuotationDialog
          open={openTransferDialog}
          serviceId={service.id}
          serviceTitle={service.serviceTitle}
          currentClientId={service.clientId}
          currentClientName={service.clientName}
          handleClose={() => setOpenTransferDialog(false)}
          handleSnackbar={handleSnackbar}
          fetchData={fetchData}
        />
      )}
      {isTabActive && openPasswordDialog && (
        <PasswordConfirmation
          open={openPasswordDialog}
          handleClose={handleCloseDelete}
          url={GET_DELETE_SERVICES_URL(Number(params))}
          title='Delete Quotation'
          message='delete quotation, redirecting to quotation list'
          handleSnackbar={handleSnackbar}
        />
      )}
      <ActionSnackbar
        variant={snackbarVarient}
        message={snackbarMessage}
        open={isTabActive && openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        Icon={snackbarVarient === 'success' ? CheckCircleIcon : ErrorIcon}
        isCountdown={snackbarIsCountdown}
        redirectPath='quotations'
      />
    </Container>
  );
};

export default ServiceDetailPage;
